import * as THREE from 'three';
import { mulberry32 } from '../../utils/random';

type LeafCluster = {
  group: THREE.Group;
  basePositions: THREE.Vector3[];
  pushOffsets: number[];
  swingPhase: number[];
  swingAmp: number;
};

export class ForegroundLeaves {
  public root: THREE.Group;
  private clusters: LeafCluster[] = [];
  private windPhase = 0;

  constructor() {
    this.root = new THREE.Group();
    const positions = this.generatePositions();
    const rng = mulberry32(7);
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const group = new THREE.Group();
      group.position.copy(pos);
      group.rotation.y = rng() * Math.PI * 2;
      group.rotation.z = (rng() - 0.5) * 0.4;
      this.root.add(group);

      const leafCount = 3 + Math.floor(rng() * 4);
      const basePositions: THREE.Vector3[] = [];
      const pushOffsets: number[] = [];
      const swingPhase: number[] = [];
      for (let j = 0; j < leafCount; j++) {
        const leaf = this.makeLeaf(rng);
        leaf.position.set(
          (rng() - 0.5) * 0.7,
          Math.random() * 1.4,
          (rng() - 0.5) * 0.7
        );
        leaf.rotation.set(
          (rng() - 0.5) * 0.5,
          rng() * Math.PI,
          (rng() - 0.5) * 0.5
        );
        group.add(leaf);
        basePositions.push(leaf.position.clone());
        pushOffsets.push(0);
        swingPhase.push(rng() * Math.PI * 2);
      }
      this.clusters.push({
        group,
        basePositions,
        pushOffsets,
        swingPhase,
        swingAmp: 0.04 + rng() * 0.05,
      });
    }
  }

  private generatePositions(): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];
    const rng = mulberry32(11);
    const count = 36;
    for (let i = 0; i < count; i++) {
      const angle = rng() * Math.PI * 2;
      const radius = 3 + rng() * 20;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      positions.push(new THREE.Vector3(x, 0, z));
    }
    for (let i = 0; i < 12; i++) {
      const angle = rng() * Math.PI * 2;
      const radius = 2 + rng() * 3;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      positions.push(new THREE.Vector3(x, 0, z));
    }
    return positions;
  }

  private makeLeaf(rng: () => number): THREE.Mesh {
    const shape = new THREE.Shape();
    const variant = rng();
    if (variant < 0.4) {
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.15, 0.4, 0.45, 0.6, 0.3, 1.2);
      shape.bezierCurveTo(0.15, 1.5, 0.0, 1.6, -0.05, 1.55);
      shape.bezierCurveTo(-0.25, 1.0, -0.15, 0.4, 0, 0);
    } else if (variant < 0.75) {
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.08, 0.5, 0.35, 0.7, 0.25, 1.4);
      shape.bezierCurveTo(0.15, 1.7, 0.05, 1.8, 0.0, 1.75);
      shape.bezierCurveTo(-0.15, 1.4, -0.1, 0.6, 0, 0);
    } else {
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.2, 0.3, 0.5, 0.5, 0.4, 1.1);
      shape.bezierCurveTo(0.3, 1.6, 0.1, 1.8, -0.05, 1.7);
      shape.bezierCurveTo(-0.3, 1.0, -0.2, 0.3, 0, 0);
    }
    const geo = new THREE.ShapeGeometry(shape, 8);
    const tintHsl = { h: 0.27 + (rng() - 0.5) * 0.05, s: 0.5, l: 0.22 + rng() * 0.12 };
    const color = new THREE.Color().setHSL(tintHsl.h, tintHsl.s, tintHsl.l);
    const mat = new THREE.MeshStandardMaterial({
      color,
      side: THREE.DoubleSide,
      roughness: 0.7,
      metalness: 0,
      transparent: true,
      opacity: 0.95,
    });
    const leaf = new THREE.Mesh(geo, mat);
    leaf.scale.setScalar(0.55 + rng() * 0.85);
    return leaf;
  }

  pushFromOrb(strength: number, orbPos: THREE.Vector3): void {
    for (const cluster of this.clusters) {
      for (let i = 0; i < cluster.basePositions.length; i++) {
        const leaf = cluster.group.children[i] as THREE.Mesh;
        if (!leaf) continue;
        const world = new THREE.Vector3();
        leaf.getWorldPosition(world);
        const dx = world.x - orbPos.x;
        const dz = world.z - orbPos.z;
        const dist = Math.hypot(dx, dz);
        if (dist > 6) continue;
        const falloff = Math.max(0, 1 - dist / 6);
        const push = strength * falloff;
        cluster.pushOffsets[i] = (cluster.pushOffsets[i] || 0) + push * 0.4;
        cluster.pushOffsets[i] = Math.min(cluster.pushOffsets[i], 2.5);
      }
    }
  }

  update(now: number, deltaMs: number, corruptionRatio: number): void {
    this.windPhase += deltaMs * 0.001 * 0.5;
    const wiltColor = new THREE.Color('#7a5530');
    for (const cluster of this.clusters) {
      for (let i = 0; i < cluster.basePositions.length; i++) {
        const leaf = cluster.group.children[i] as THREE.Mesh;
        if (!leaf) continue;
        const base = cluster.basePositions[i];
        const swing = Math.sin(this.windPhase + cluster.swingPhase[i]) * cluster.swingAmp;
        const push = cluster.pushOffsets[i] || 0;
        const pushDirX = cluster.group.position.x >= 0 ? 1 : -1;
        const pushDirZ = cluster.group.position.z >= 0 ? 1 : -1;
        leaf.position.set(
          base.x + (pushDirX * push * 0.5),
          base.y + swing,
          base.z + (pushDirZ * push * 0.5)
        );
        cluster.pushOffsets[i] *= 0.92;

        const mat = leaf.material as THREE.MeshStandardMaterial;
        if (mat.color) {
          const t = Math.min(1, corruptionRatio * 1.2);
          mat.color.lerp(wiltColor, t * 0.4);
        }
      }
    }
  }
}
